import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, FileText, PlayCircle, Link as LinkIcon, Wallet, ChevronRight, Pencil, Save } from "lucide-react";
import { toast } from "sonner";

function PartnerEditor({ partner, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: partner.name,
    tagline: partner.tagline || "",
    color: partner.color || "#1B2E7A",
    website: partner.website || "",
    summary: partner.summary || "",
    approval_time: partner.approval_time || "",
    credit_required: partner.credit_required || "",
    key_features: (partner.key_features || []).join("\n"),
  });

  const submit = async () => {
    try {
      await api.patch(`/admin/financing-partners/${partner.id}`, {
        ...form,
        key_features: form.key_features.split("\n").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Socio actualizado");
      onSaved();
    } catch {
      toast.error("Error al guardar");
    }
  };

  return (
    <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-auto">
      <DialogHeader>
        <DialogTitle className="font-heading">Editar {partner.name}</DialogTitle>
      </DialogHeader>
      <div className="space-y-3">
        <Input placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Input placeholder="Eslogan" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} />
        <div className="flex gap-2 items-center">
          <Input placeholder="#1B2E7A" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="max-w-[140px]" />
          <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 w-12 cursor-pointer" />
          <span className="text-xs text-[#737373]">Color de marca</span>
        </div>
        <Input placeholder="Sitio web (https://...)" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
        <Textarea placeholder="Resumen breve" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Tiempo de aprobación" value={form.approval_time} onChange={(e) => setForm({ ...form, approval_time: e.target.value })} />
          <Input placeholder="Crédito requerido" value={form.credit_required} onChange={(e) => setForm({ ...form, credit_required: e.target.value })} />
        </div>
        <div>
          <p className="text-xs text-[#6B7280] mb-1">Características clave (una por línea)</p>
          <Textarea
            value={form.key_features}
            onChange={(e) => setForm({ ...form, key_features: e.target.value })}
            rows={5}
            placeholder="Aprobación rápida&#10;Sin crédito mínimo&#10;..."
          />
        </div>
      </div>
      <DialogFooter>
        <Button onClick={submit} className="bg-[#1B2E7A] hover:bg-[#13205A] text-white" data-testid="save-partner">
          <Save className="h-4 w-4 mr-2" /> Guardar
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function ResourceForm({ partnerId, onAdded }) {
  const fileRef = useRef();
  const [form, setForm] = useState({
    title: "", resource_type: "pdf", url_type: "upload", url: "", notes: "",
  });
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((p) => ({ ...p, url: data.path, url_type: "upload" }));
      toast.success("Archivo subido");
    } catch {
      toast.error("Error al subir");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.title || !form.url) {
      toast.error("Título y archivo/URL requeridos");
      return;
    }
    try {
      await api.post(`/admin/financing-partners/${partnerId}/resources`, form);
      toast.success("Recurso agregado");
      setForm({ title: "", resource_type: "pdf", url_type: "upload", url: "", notes: "" });
      if (fileRef.current) fileRef.current.value = "";
      onAdded();
    } catch {
      toast.error("Error al agregar");
    }
  };

  const acceptByType = form.resource_type === "pdf" ? "application/pdf" : "video/*";

  return (
    <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-md p-4 space-y-2.5">
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Nuevo recurso</p>
      <div className="grid sm:grid-cols-2 gap-2">
        <select
          value={form.resource_type}
          onChange={(e) => setForm({ ...form, resource_type: e.target.value, url: "", url_type: e.target.value === "link" ? "url" : "upload" })}
          className="border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white"
          data-testid="res-type-select"
        >
          <option value="pdf">PDF</option>
          <option value="video">Video</option>
          <option value="link">Enlace externo</option>
        </select>
        <Input placeholder="Título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="res-title-input" />
      </div>
      {form.resource_type === "link" || form.url_type === "url" ? (
        <Input placeholder="URL https://..." value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="res-url-input" />
      ) : null}
      {form.resource_type !== "link" && (
        <div className="flex items-center gap-2">
          <select
            value={form.url_type}
            onChange={(e) => setForm({ ...form, url_type: e.target.value, url: "" })}
            className="border border-[#E5E7EB] rounded-md px-3 py-1.5 text-xs bg-white"
          >
            <option value="upload">Subir archivo</option>
            <option value="url">URL externa</option>
          </select>
          {form.url_type === "upload" && (
            <>
              <input ref={fileRef} type="file" accept={acceptByType} onChange={handleUpload} className="text-sm" data-testid="res-file-input" />
              {uploading && <span className="text-xs text-[#737373]">Subiendo...</span>}
              {form.url && !uploading && <span className="text-xs text-[#1B2E7A]">Listo</span>}
            </>
          )}
          {form.url_type === "url" && (
            <Input placeholder="URL https://..." value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="flex-1" />
          )}
        </div>
      )}
      <Input placeholder="Notas para el vendedor (opcional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
      <Button onClick={submit} className="bg-[#1B2E7A] hover:bg-[#13205A] text-white text-sm" data-testid="add-resource-button">
        <Plus className="h-4 w-4 mr-1" /> Agregar recurso
      </Button>
    </div>
  );
}

export default function AdminFinancing() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [resourcesByPartner, setResourcesByPartner] = useState({});
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/financing-partners");
      setPartners(r.data);
    } finally {
      setLoading(false);
    }
  };

  const loadResources = async (pid) => {
    const r = await api.get(`/financing-partners/${pid}`);
    setResourcesByPartner((prev) => ({ ...prev, [pid]: r.data.resources || [] }));
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = async (pid) => {
    if (expanded === pid) {
      setExpanded(null);
    } else {
      setExpanded(pid);
      if (!resourcesByPartner[pid]) await loadResources(pid);
    }
  };

  const removeResource = async (pid, rid) => {
    if (!window.confirm("¿Eliminar recurso?")) return;
    await api.delete(`/admin/financing-partners/${pid}/resources/${rid}`);
    toast.success("Recurso eliminado");
    await loadResources(pid);
    await load();
  };

  return (
    <div data-testid="admin-financing-page">
      <div className="mb-10 fade-up">
        <p className="text-xs uppercase tracking-[0.2em] text-[#6B7280] mb-2 inline-flex items-center gap-1.5">
          <Wallet className="h-3.5 w-3.5" /> Solo administradores
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1A1A] mb-3">
          Materiales de Financiamiento.
        </h1>
        <p className="text-[#374151] max-w-2xl leading-relaxed">
          Gestiona los 8 socios de financiamiento. Para cada uno puedes editar la información de marca
          y agregar PDFs, videos o enlaces que tus vendedores podrán consultar en piso.
        </p>
      </div>

      {loading ? (
        <p className="text-[#374151]">Cargando...</p>
      ) : (
        <div className="space-y-4">
          {partners.map((p) => {
            const isOpen = expanded === p.id;
            const resources = resourcesByPartner[p.id] || [];
            return (
              <div key={p.id} className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden" data-testid={`admin-partner-${p.id}`}>
                <button
                  onClick={() => toggle(p.id)}
                  className="w-full flex items-center gap-4 p-4 hover:bg-[#FFFFFF] transition-colors text-left"
                >
                  <div
                    className="h-12 w-12 rounded-md flex items-center justify-center text-white font-heading text-lg shrink-0"
                    style={{ background: p.color || "#1B2E7A" }}
                  >
                    {p.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-lg text-[#1A1A1A]">{p.name}</h3>
                    <p className="text-xs text-[#737373] truncate">{p.tagline}</p>
                  </div>
                  <span className="text-xs text-[#737373]">
                    {p.resources_count || 0} {p.resources_count === 1 ? "recurso" : "recursos"}
                  </span>
                  <ChevronRight className={`h-4 w-4 text-[#6B7280] transition-transform ${isOpen ? "rotate-90" : ""}`} />
                </button>

                {isOpen && (
                  <div className="border-t border-[#E5E7EB] p-5 space-y-5 bg-[#FCFAF6]">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="max-w-2xl">
                        <p className="text-sm text-[#374151] mb-2">{p.summary}</p>
                        <p className="text-xs text-[#737373]">
                          Sitio: {p.website || "—"} · Aprobación: {p.approval_time || "—"} · Crédito: {p.credit_required || "—"}
                        </p>
                      </div>
                      <Dialog open={editing === p.id} onOpenChange={(o) => setEditing(o ? p.id : null)}>
                        <DialogTrigger asChild>
                          <Button variant="outline" className="border-[#E5E7EB]" data-testid={`edit-partner-${p.id}`}>
                            <Pencil className="h-3.5 w-3.5 mr-1.5" /> Editar marca
                          </Button>
                        </DialogTrigger>
                        {editing === p.id && (
                          <PartnerEditor
                            partner={p}
                            onClose={() => setEditing(null)}
                            onSaved={async () => {
                              setEditing(null);
                              await load();
                            }}
                          />
                        )}
                      </Dialog>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-5">
                      <ResourceForm
                        partnerId={p.id}
                        onAdded={async () => {
                          await loadResources(p.id);
                          await load();
                        }}
                      />
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-2">Recursos cargados</p>
                        {resources.length === 0 ? (
                          <p className="text-sm text-[#737373]">Aún no hay recursos.</p>
                        ) : (
                          <div className="space-y-2">
                            {resources.map((r) => {
                              const Icon =
                                r.resource_type === "video" ? PlayCircle :
                                r.resource_type === "pdf" ? FileText : LinkIcon;
                              return (
                                <div key={r.id} className="flex items-center gap-2 bg-white border border-[#E5E7EB] rounded-md p-3">
                                  <Icon className="h-4 w-4 text-[#1B2E7A] shrink-0" strokeWidth={1.5} />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-[#1A1A1A] truncate">{r.title}</p>
                                    <p className="text-xs text-[#6B7280] truncate">
                                      {r.resource_type.toUpperCase()} · {r.url_type === "url" ? "Enlace" : "Archivo subido"}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => removeResource(p.id, r.id)}
                                    className="text-[#DC2626] hover:bg-[#DC2626]/10 p-1.5 rounded-md"
                                    data-testid={`del-res-${r.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    <Link
                      to={`/financiamiento/${p.id}`}
                      className="inline-flex items-center gap-1 text-xs text-[#1B2E7A] hover:underline"
                    >
                      Ver vista de vendedor →
                    </Link>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
